import axios from 'axios';

export interface ApiErrorResponse {
    timestamp: string;
    status: number;
    errorCode: string;
    message: string;
    path: string;
    details?: { field: string; message: string }[];
}

/** Un egreso dejaría una cuenta financiera física en negativo; `details` indica el campo. */
export const INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE';
/** Un consumo de stock dejaría el balance de un artículo en el sector por debajo de cero; `details` indica el campo. */
export const INSUFFICIENT_STOCK = 'INSUFFICIENT_STOCK';
/** El monto declarado de la caja no coincide con su saldo y falta confirmar la diferencia; `details` indica el campo. */
export const CASH_DISCREPANCY_NOT_CONFIRMED = 'CASH_DISCREPANCY_NOT_CONFIRMED';
/** El establecimiento elegido como predeterminado en Mi perfil ya no está disponible. */
export const DEFAULT_BUILDING_NOT_SELECTABLE = 'DEFAULT_BUILDING_NOT_SELECTABLE';

const NETWORK_ERROR_MESSAGE = 'No se pudo conectar con el servidor. Verificá tu conexión e intentá nuevamente.';
const UNKNOWN_ERROR_MESSAGE = 'Ocurrió un error inesperado. Intentá nuevamente.';

function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
    if (typeof data !== 'object' || data === null) return false;
    const candidate = data as Record<string, unknown>;
    return typeof candidate.errorCode === 'string'
        && typeof candidate.message === 'string'
        && typeof candidate.status === 'number';
}

/**
 * Traduce cualquier error de una llamada HTTP al contrato ApiErrorResponse.
 * Si la respuesta no respeta el contrato (timeout, sin conexión, proxy), devuelve
 * un mensaje genérico propio del frontend en lugar del texto crudo de la excepción.
 */
export function parseApiError(error: unknown): ApiErrorResponse {
    let apiError: ApiErrorResponse;

    if (axios.isAxiosError(error) && isApiErrorResponse(error.response?.data)) {
        apiError = error.response.data;
    } else if (axios.isAxiosError(error) && error.response) {
        apiError = {
            timestamp: new Date().toISOString(),
            status: error.response.status,
            errorCode: 'UNKNOWN_ERROR',
            message: UNKNOWN_ERROR_MESSAGE,
            path: error.config?.url ?? '',
        };
    } else if (axios.isAxiosError(error)) {
        apiError = {
            timestamp: new Date().toISOString(),
            status: 0,
            errorCode: 'NETWORK_ERROR',
            message: NETWORK_ERROR_MESSAGE,
            path: error.config?.url ?? '',
        };
    } else {
        apiError = {
            timestamp: new Date().toISOString(),
            status: 0,
            errorCode: 'UNKNOWN_ERROR',
            message: UNKNOWN_ERROR_MESSAGE,
            path: '',
        };
    }

    console.error(`[API] ${apiError.errorCode} ${apiError.path}`, error);
    return apiError;
}

/** Mensaje que el backend asoció a un campo en `details`, si lo hay. */
export function getFieldErrorMessage(apiError: ApiErrorResponse, field: string): string | undefined {
    return apiError.details?.find(detail => detail.field === field)?.message;
}
